/**
 * ShimmerEffect Component
 *
 * Premium shine sweep effect for highlighting elements.
 * Features:
 * - Diagonal light sweep animation
 * - Configurable timing and appearance
 * - Can be applied as overlay or mask
 * - Perfect for prices, badges, and product highlights
 */

import React from 'react';
import { useCurrentFrame, useVideoConfig, interpolate, AbsoluteFill } from 'remotion';

export interface ShimmerEffectProps {
  /** Width of the shimmer band in pixels */
  width?: number;
  /** Shimmer color (typically white or light) */
  color?: string;
  /** Delay before shimmer starts (in frames) */
  delay?: number;
  /** Duration of one shimmer sweep (in frames) */
  duration?: number;
  /** Number of times to repeat the shimmer */
  repeatCount?: number;
  /** Gap between shimmer repeats (in frames) */
  repeatGap?: number;
  /** Angle of the shimmer sweep (degrees) */
  angle?: number;
  /** Intensity/opacity of the shimmer (0-1) */
  intensity?: number;
  /** Start frame */
  startFrame?: number;
  /** Blur amount for softer shimmer */
  blur?: number;
}

export const ShimmerEffect: React.FC<ShimmerEffectProps> = ({
  width = 80,
  color = 'rgba(255, 255, 255, 0.4)',
  delay = 0,
  duration = 30,
  repeatCount = 1,
  repeatGap = 90,
  angle = -20,
  intensity = 0.6,
  startFrame = 0,
  blur = 10,
}) => {
  const frame = useCurrentFrame();

  const relativeFrame = frame - startFrame - delay;

  if (relativeFrame < 0) {
    return null;
  }

  // Calculate which repeat we're in
  const totalCycleLength = duration + repeatGap;
  const currentCycle = Math.floor(relativeFrame / totalCycleLength);
  const frameInCycle = relativeFrame % totalCycleLength;

  // Only render during active shimmer phase (not during gap)
  if (frameInCycle >= duration || currentCycle >= repeatCount) {
    return null;
  }

  // Calculate shimmer position (-100% to 200% of container width)
  const shimmerProgress = interpolate(
    frameInCycle,
    [0, duration],
    [-50, 150],
    { extrapolateRight: 'clamp' }
  );

  // Fade in and out at edges
  const shimmerOpacity = interpolate(
    shimmerProgress,
    [-50, 0, 100, 150],
    [0, 1, 1, 0],
    { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' }
  );

  return (
    <AbsoluteFill
      style={{
        pointerEvents: 'none',
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          position: 'absolute',
          top: '-50%',
          left: `${shimmerProgress}%`,
          width: width,
          height: '200%',
          background: `linear-gradient(
            ${angle}deg,
            transparent 0%,
            ${color} 45%,
            ${color} 55%,
            transparent 100%
          )`,
          opacity: shimmerOpacity * intensity,
          transform: `translateX(-50%) skewX(${angle}deg)`,
          filter: `blur(${blur}px)`,
        }}
      />
    </AbsoluteFill>
  );
};

/**
 * ShimmerOverlay - Apply shimmer to any element via wrapper
 */
export interface ShimmerOverlayProps {
  /** Children to wrap with shimmer */
  children: React.ReactNode;
  /** Shimmer configuration */
  shimmerProps?: Omit<ShimmerEffectProps, 'startFrame'>;
  /** Start frame for the shimmer */
  startFrame?: number;
  /** Container width */
  width?: number | string;
  /** Container height */
  height?: number | string;
  /** Border radius for rounded corners */
  borderRadius?: number;
}

export const ShimmerOverlay: React.FC<ShimmerOverlayProps> = ({
  children,
  shimmerProps = {},
  startFrame = 0,
  width = 'auto',
  height = 'auto',
  borderRadius = 0,
}) => {
  return (
    <div
      style={{
        position: 'relative',
        display: 'inline-block',
        width,
        height,
        overflow: 'hidden',
        borderRadius,
      }}
    >
      {children}
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          overflow: 'hidden',
          borderRadius,
          pointerEvents: 'none',
        }}
      >
        <ShimmerEffect {...shimmerProps} startFrame={startFrame} />
      </div>
    </div>
  );
};

/**
 * PremiumShine - Gold/luxury shimmer preset
 */
export interface PremiumShineProps {
  /** Start frame */
  startFrame?: number;
  /** Delay before shine */
  delay?: number;
  /** Number of shine passes */
  passes?: number;
}

export const PremiumShine: React.FC<PremiumShineProps> = ({
  startFrame = 0,
  delay = 30,
  passes = 2,
}) => {
  return (
    <ShimmerEffect
      width={100}
      color="rgba(255, 215, 0, 0.3)"
      delay={delay}
      duration={40}
      repeatCount={passes}
      repeatGap={60}
      angle={-25}
      intensity={0.7}
      startFrame={startFrame}
      blur={15}
    />
  );
};

/**
 * SubtleGleam - Subtle white shimmer preset
 */
export interface SubtleGleamProps {
  /** Start frame */
  startFrame?: number;
  /** Delay before gleam */
  delay?: number;
}

export const SubtleGleam: React.FC<SubtleGleamProps> = ({
  startFrame = 0,
  delay = 0,
}) => {
  return (
    <ShimmerEffect
      width={60}
      color="rgba(255, 255, 255, 0.25)"
      delay={delay}
      duration={25}
      repeatCount={1}
      repeatGap={0}
      angle={-15}
      intensity={0.5}
      startFrame={startFrame}
      blur={8}
    />
  );
};

/**
 * TextShimmer - Special shimmer for text elements
 */
export interface TextShimmerProps {
  /** Text content */
  text: string;
  /** Font size */
  fontSize?: number;
  /** Font weight */
  fontWeight?: number;
  /** Text color */
  color?: string;
  /** Shimmer start frame */
  startFrame?: number;
  /** Shimmer delay */
  delay?: number;
  /** Font family */
  fontFamily?: string;
}

export const TextShimmer: React.FC<TextShimmerProps> = ({
  text,
  fontSize = 32,
  fontWeight = 700,
  color = '#ffffff',
  startFrame = 0,
  delay = 30,
  fontFamily = 'Inter, sans-serif',
}) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const relativeFrame = frame - startFrame - delay;

  // Shimmer position across text
  const shimmerPosition = interpolate(
    relativeFrame,
    [0, fps * 1],
    [-100, 200],
    { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' }
  );

  return (
    <div
      style={{
        position: 'relative',
        display: 'inline-block',
      }}
    >
      {/* Base text */}
      <span
        style={{
          fontSize,
          fontWeight,
          color,
          fontFamily,
        }}
      >
        {text}
      </span>

      {/* Shimmer overlay with clip-path to text */}
      {relativeFrame >= 0 && relativeFrame < fps * 1 && (
        <span
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            fontSize,
            fontWeight,
            fontFamily,
            background: `linear-gradient(
              90deg,
              transparent ${shimmerPosition - 30}%,
              rgba(255, 255, 255, 0.8) ${shimmerPosition}%,
              transparent ${shimmerPosition + 30}%
            )`,
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            pointerEvents: 'none',
          }}
        >
          {text}
        </span>
      )}
    </div>
  );
};

/**
 * PulsingGlow - Subtle pulsing glow effect
 */
export interface PulsingGlowProps {
  /** Glow color */
  color?: string;
  /** Pulse speed (cycles per second) */
  speed?: number;
  /** Glow intensity (0-1) */
  intensity?: number;
  /** Start frame */
  startFrame?: number;
  /** Glow blur radius */
  blurRadius?: number;
}

export const PulsingGlow: React.FC<PulsingGlowProps> = ({
  color = 'rgba(255, 107, 53, 0.5)',
  speed = 1,
  intensity = 0.6,
  startFrame = 0,
  blurRadius = 30,
}) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const relativeFrame = frame - startFrame;

  if (relativeFrame < 0) {
    return null;
  }

  // Entrance fade
  const entranceOpacity = interpolate(
    relativeFrame,
    [0, fps * 0.5],
    [0, 1],
    { extrapolateRight: 'clamp' }
  );

  // Pulsing animation (sine wave)
  const cyclesPerFrame = speed / fps;
  const pulse = (Math.sin(relativeFrame * cyclesPerFrame * Math.PI * 2) + 1) / 2;

  const glowOpacity = 0.3 + pulse * 0.7;

  return (
    <AbsoluteFill
      style={{
        pointerEvents: 'none',
        boxShadow: `inset 0 0 ${blurRadius}px ${color}`,
        opacity: glowOpacity * intensity * entranceOpacity,
      }}
    />
  );
};

/**
 * EdgeHighlight - Glowing edge effect
 */
export interface EdgeHighlightProps {
  /** Edge color */
  color?: string;
  /** Which edge to highlight */
  edge?: 'top' | 'bottom' | 'left' | 'right' | 'all';
  /** Highlight width */
  size?: number;
  /** Intensity (0-1) */
  intensity?: number;
  /** Start frame */
  startFrame?: number;
  /** Enable animation */
  animate?: boolean;
}

export const EdgeHighlight: React.FC<EdgeHighlightProps> = ({
  color = 'rgba(255, 215, 0, 0.6)',
  edge = 'top',
  size = 3,
  intensity = 0.8,
  startFrame = 0,
  animate = true,
}) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const relativeFrame = frame - startFrame;

  if (relativeFrame < 0) {
    return null;
  }

  // Entrance animation
  const entranceProgress = interpolate(
    relativeFrame,
    [0, fps * 0.5],
    [0, 1],
    { extrapolateRight: 'clamp' }
  );

  // Subtle pulse if animated
  const pulse = animate
    ? 0.7 + Math.sin(relativeFrame * 0.08) * 0.3
    : 1;

  const getGradient = (e: typeof edge): string => {
    const gradients: Record<typeof edge, string> = {
      top: `linear-gradient(to bottom, ${color}, transparent)`,
      bottom: `linear-gradient(to top, ${color}, transparent)`,
      left: `linear-gradient(to right, ${color}, transparent)`,
      right: `linear-gradient(to left, ${color}, transparent)`,
      all: `none`,
    };
    return gradients[e];
  };

  if (edge === 'all') {
    return (
      <AbsoluteFill
        style={{
          pointerEvents: 'none',
          boxShadow: `inset 0 0 ${size * 5}px ${color}`,
          opacity: intensity * entranceProgress * pulse,
        }}
      />
    );
  }

  const edgeStyles: React.CSSProperties = {
    position: 'absolute',
    background: getGradient(edge),
    opacity: intensity * entranceProgress * pulse,
    filter: `blur(${size}px)`,
    ...(edge === 'top' && { top: 0, left: 0, right: 0, height: size * 10 }),
    ...(edge === 'bottom' && { bottom: 0, left: 0, right: 0, height: size * 10 }),
    ...(edge === 'left' && { top: 0, bottom: 0, left: 0, width: size * 10 }),
    ...(edge === 'right' && { top: 0, bottom: 0, right: 0, width: size * 10 }),
  };

  return (
    <AbsoluteFill style={{ pointerEvents: 'none', overflow: 'hidden' }}>
      <div style={edgeStyles} />
    </AbsoluteFill>
  );
};
